import React, {useEffect, useMemo, useRef} from 'react'
import _ from 'lodash/fp'
import {Fill} from '@startlibs/core'
import {Button, Popup, DefaultArrow} from '@startlibs/components'
import {TextInput, Errors, WithForm} from '@startlibs/form'
import styled from 'styled-components/macro'
import { Editor, Transforms } from "slate"
import {PathUtils, unwrapLink, wrapLink} from "../slateUtils";
import {linkRegex, useEventListener} from "@startlibs/utils";
import {useDecorationToggle} from "../WithDecoration";
import {ReactEditor} from "slate-react";
import {FormattedMessage, useIntl} from "react-intl";

const Arrow = styled(DefaultArrow)`
  top: -1rem;
  box-shadow: -1px -1px 2px -1px rgb(0 0 0 / 25%);
`

const StyledPopup = styled(Popup)`
  text-align: right;
  ${Button} {
    margin-top: 1rem;
  }
`

const preValidation = (intl) => (formValues) => {
  const linkTextError = intl.formatMessage({
    defaultMessage: 'Text can not be empty',
    description: 'Text editor, link popup, text can not be empty error'
  })
  const linkUrlError = intl.formatMessage({
    defaultMessage: 'Please check the URL',
    description: 'Text editor, link popup, please check the URL error'
  })
  let errors
  if (!formValues.text) {
    errors = {'text': [linkTextError]}
  }
  if (!formValues.url || formValues.url === ' ' || !linkRegex.test(formValues.url.trim())) {
    errors = {...errors, 'url': [linkUrlError]}
  }
  return errors
}

const addHttp = (s) => {
  s = s.replace(/^\s/, '')
  if (s.indexOf('http://') === 0 || s.indexOf('https://') === 0) {
    return s
  } else {
    return 'http://' + s
  }
}

const transform = _.update('url',addHttp)

export const LinkPopup = ({editor, isActive, values, range, closePopup}) => {
  const popupRef = useRef()
  const ignoredFirst = useRef()
  const decoration = useDecorationToggle()
  const intl = useIntl()

  const valuesWithSelection = useMemo(() => {
    if (values) {
      return values
    }
    if (editor.selection) {
      const range = PathUtils.getFirstBlockRange(editor)
      Transforms.select(editor,range)
      const text = Editor.string(editor,range)
      return {text}
    }
    return {}
  },[])

  useEffect(() => {

    const el = popupRef.current
    const domRange = ReactEditor.toDOMRange(editor,editor.selection)
    const rect = domRange.getBoundingClientRect()
    el.style.opacity = '1'
    el.style.width = `340px`
    el.style.top = `${rect.bottom + 6 + window.pageYOffset}px`
    el.style.left = `${rect.left + window.pageXOffset - (el.offsetWidth / 2) + (rect.width / 2)}px`
    el.position = 'absolute'

    const [textInput,urlInput] = el.querySelectorAll("input[type=text]")
    if (!valuesWithSelection.url || valuesWithSelection.text) {
      urlInput.focus()
    } else {
      textInput.focus()
    }
  }, [])

  useEventListener('click',(e) => {
    if (e.target !== popupRef.current && !popupRef.current.contains(e.target) && ignoredFirst.current) {
      closePopup(false)
    }
    ignoredFirst.current = true
  })

  useEffect(() => {
    decoration.openWith([{
      ...(range || editor.selection),
      linkHighlight: true
    }])
    return () => decoration.close()
  },[])

  const confirm = ({text,url}) => {
    if (range) {
      Transforms.select(editor,range)
    }
    wrapLink(editor,url,text)
    closePopup()
  }
  const remove = ({text,url}) => {
    unwrapLink(editor)
    closePopup()
  }

  return <Fill name='Popup'>
    <StyledPopup skipPortal ref={popupRef}>
      <Arrow/>
      <WithForm values={valuesWithSelection} action={confirm} preValidation={preValidation(intl)} transform={transform}>
        <TextInput path="text" label={intl.formatMessage({
          defaultMessage: 'Text to display',
          description: 'Text editor, link popup, text to display'
        })} mandatory/>
        <TextInput path="url" label={intl.formatMessage({
          defaultMessage: "Url",
          description: "Text editor, link popup, url"
        })} mandatory/>
        <Errors/>
        <Button onClick={isActive ? remove : closePopup}>{intl.formatMessage({
          defaultMessage: "Remove link",
          description: "Text editor, link popup, remove link"
        })} </Button>
        <Button onClick={closePopup}><FormattedMessage
          defaultMessage="Cancel"
          description="Cancel button"
        /></Button>
        <Button type="submit" highlight><FormattedMessage
          defaultMessage="Apply"
          description="Apply button"
        /></Button>
      </WithForm>
    </StyledPopup>
  </Fill>
}